import React, { useEffect } from "react";
import { Card, Form, Input, Button, Row, Col, message, Tabs } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "antd";
import { useResumeStore } from "../../store/resumeStore";
import { mapApiResumeToEditor } from "./mapParsedResumeToState";
import useViewportWidth from "../../hooks/useViewportWidth";


const { TabPane } = Tabs;
const { useToken } = theme;

function getImportEducationText(education = []) {
  return education
    .map((edu) => {
      const degree =
        edu.degree ||
        edu.course ||
        edu.qualification ||
        edu.title ||
        edu.accreditation?.education ||
        "";
      const institution =
        edu.institution ||
        edu.organization ||
        edu.university ||
        edu.college ||
        edu.school ||
        edu.name ||
        "";
      const grade = edu.grade?.value || edu.score || edu.gpa || "";

      return [degree, institution, grade ? `(${grade})` : ""]
        .filter(Boolean)
        .join(" - ");
    })
    .filter(Boolean)
    .join("\n");
}

export default function ImportReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { token } = useToken();
  const viewportWidth = useViewportWidth();
  const isCompact = viewportWidth <= 768;
  const importedData = location.state?.importedData;

  /* ================= LOAD IMPORTED DATA ================= */

  useEffect(() => {
    if (!importedData?.rawApiResponse?.data) {
      message.error("No imported data found. Please upload resume again.");
      navigate("/dashboard");
      return;
    }

    const apiData = importedData.rawApiResponse.data;

    const mappedData = {
      name: apiData.name
        ? `${apiData.name.first || ""} ${apiData.name.last || ""}`.trim()
        : "",

      profession: apiData.profession || "",

      currentCompany:
        apiData.workExperience?.[0]?.organization || "",

      email: apiData.emails?.[0] || "",

      phone: apiData.phoneNumbers?.[0] || "",

      summary: apiData.summary || "",

      experience: apiData.workExperience?.length
        ? apiData.workExperience
            .map((job) => {
              const title = job.jobTitle || "";
              const company = job.organization || "";
              const start = job?.dates?.startDate || "";
              const end = job?.dates?.endDate || "Present";
              const desc = job.jobDescription || "";

              return `${title} at ${company}
${start} - ${end}

${desc}`;
            })
            .join("\n\n----------------------\n\n")
        : "",

      skills: apiData.skills?.length
        ? apiData.skills.map((s) => s.name).join(", ")
        : "",

      education: apiData.education?.length
        ? getImportEducationText(apiData.education)
        : "",
    };

    form.setFieldsValue(mappedData);
  }, [importedData, form, navigate]);

  /* ================= SUBMIT ================= */
const setResume = useResumeStore((s) => s.setResume);
const resetTemplateSelection = useResumeStore((s) => s.resetTemplateSelection);
const onFinish = (values) => {

  const mappedResume = mapApiResumeToEditor(importedData);

  resetTemplateSelection();
  setResume(mappedResume);   // ⭐ SAVE TO GLOBAL STORE

  navigate("/resume/new");
};


  if (!importedData) return null;

  return (
  <div
    style={{
      minHeight: "100vh",
      background: token.colorBgLayout,
      paddingTop: isCompact ? 16 : 40,
      paddingBottom: isCompact ? 16 : 40,
      paddingInline: isCompact ? 12 : 0,
      transition: "all 0.3s ease",
    }}
  >
    <Row justify="center">
      <Col xs={24} sm={22} md={20} lg={16}>
        <Card
          title="Review & Edit Imported Resume Data"
          style={{
            background: token.colorBgContainer,
            borderRadius: isCompact ? 10 : 12,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Tabs defaultActiveKey="personal" type={isCompact ? "line" : "card"}>
              <TabPane tab="Personal Info" key="personal">
                <Form.Item name="name" label="Full Name">
                  <Input />
                </Form.Item>

                <Form.Item name="profession" label="Current Title">
                  <Input />
                </Form.Item>

                <Form.Item name="currentCompany" label="Current Company">
                  <Input />
                </Form.Item>

                <Form.Item name="email" label="Email">
                  <Input />
                </Form.Item>

                <Form.Item name="phone" label="Phone">
                  <Input />
                </Form.Item>
              </TabPane>

              <TabPane tab="Summary" key="summary">
                <Form.Item name="summary" label="Professional Summary">
                  <Input.TextArea rows={6} />
                </Form.Item>
              </TabPane>

              <TabPane tab="Experience" key="experience">
                <Form.Item name="experience" label="Experience">
                  <Input.TextArea rows={14} />
                </Form.Item>
              </TabPane>

              <TabPane tab="Skills" key="skills">
                <Form.Item name="skills" label="Skills">
                  <Input.TextArea rows={6} />
                </Form.Item>
              </TabPane>

              <TabPane tab="Education" key="education">
                <Form.Item name="education" label="Education">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </TabPane>
            </Tabs>

            <Row justify="end" style={{ marginTop: 24 }}>
              <Button type="primary" htmlType="submit">
                Continue to Builder
              </Button>
            </Row>
          </Form>
        </Card>
      </Col>
    </Row>
  </div>
);
}
